import {
  UP,
  add,
  deltaAngle,
  directionFromYaw,
  dot,
  flatten,
  length,
  lerp,
  normalize,
  scale,
  smoothFactor,
  sub,
  v3,
  yawDegrees,
  type Vec3,
} from './math';

/** How a UX window (welcome card, training step form) is placed in the world. */
export type WindowPlacementMode = 'fixed' | 'headLocked';

/** The viewer's pose this frame, in world space. `forward` is the look direction. */
export interface ViewerPose {
  readonly position: Vec3;
  readonly forward: Vec3;
  readonly right: Vec3;
}

/** The thing being placed - structurally a three.js Object3D (position + lookAt). */
export interface Placeable {
  readonly position: { x: number; y: number; z: number; set(x: number, y: number, z: number): void };
  lookAt(x: number, y: number, z: number): void;
}

export interface WindowFollowerOptions {
  readonly mode: WindowPlacementMode;
  /** How far in front of the user the window is placed, in meters. */
  readonly distanceMeters: number;
  /** Vertical offset from eye height, in meters. */
  readonly heightOffsetMeters?: number;
  /** Smooth-follow response time in seconds (time constant of the lazy follow). */
  readonly followSeconds?: number;
  /** Minimum angle in degrees kept between the window's view direction and any active world label. */
  readonly clearanceDegrees?: number;
  /** In fixed mode, glide the window back in front of the user when it ends up behind them or far away. */
  readonly autoRecover?: boolean;
}

/**
 * Places a world-space UX window per the configured mode - port of the Unity
 * `WindowFollower`:
 *
 * - fixed: anchored in front of the user whenever the owner calls `reanchor`
 *   (step change, re-present) and then stays put. With `autoRecover` it also
 *   glides back into view if the user leaves it far behind.
 * - headLocked: lazily follows the user's view. Label-aware: while a
 *   registered world label is in view, the window's direction is clamped so
 *   it never encroaches on the label - it slides to a stop at the clearance
 *   boundary like a sliding door, and resumes once the user looks back.
 *
 * World labels register themselves as obstacles via the shared registry.
 */
export class WindowFollower {
  /** Positions of active obstacles (world labels) - shared across followers, like the Unity static list. */
  private static readonly obstacles = new Map<object, () => Vec3 | null>();

  private readonly target: Placeable;
  private readonly options: Required<WindowFollowerOptions>;
  private anchored = false;
  private recovering = false;

  constructor(target: Placeable, options: WindowFollowerOptions) {
    this.target = target;
    this.options = {
      mode: options.mode,
      distanceMeters: options.distanceMeters,
      heightOffsetMeters: options.heightOffsetMeters ?? 0,
      followSeconds: Math.max(0.01, options.followSeconds ?? 0.3),
      clearanceDegrees: Math.max(0, options.clearanceDegrees ?? 25),
      autoRecover: options.autoRecover ?? false,
    };
  }

  /** Make a world label (or any world UX) a no-go zone for head-locked windows. The getter returns null while inactive. */
  static registerObstacle(key: object, position: () => Vec3 | null): void {
    WindowFollower.obstacles.set(key, position);
  }

  static unregisterObstacle(key: object): void {
    WindowFollower.obstacles.delete(key);
  }

  /** Test hook: forget every obstacle. */
  static clearObstacles(): void {
    WindowFollower.obstacles.clear();
  }

  get mode(): WindowPlacementMode {
    return this.options.mode;
  }

  /** Snap the window to its desired pose now (show / re-present / step change). */
  reanchor(viewer: ViewerPose): void {
    this.moveTo(this.desiredPosition(viewer), viewer);
    this.anchored = true;
    this.recovering = false;
  }

  /** Per-frame update. */
  update(viewer: ViewerPose, deltaSeconds: number): void {
    if (!this.anchored) {
      this.reanchor(viewer);
      return;
    }

    if (this.options.mode === 'headLocked') {
      this.smoothFollow(viewer, deltaSeconds);
      return;
    }

    // fixed: stay put, but optionally glide back when left far behind.
    if (this.options.autoRecover) {
      if (!this.recovering && this.isLost(viewer)) this.recovering = true;
      if (this.recovering) this.recovering = !this.smoothFollow(viewer, deltaSeconds);
    }
  }

  /** Lazy exponential follow toward the (label-clamped) desired pose. True once settled. */
  private smoothFollow(viewer: ViewerPose, deltaSeconds: number): boolean {
    const desired = this.desiredPosition(viewer);
    const t = smoothFactor(deltaSeconds, this.options.followSeconds);
    const current = v3(this.target.position.x, this.target.position.y, this.target.position.z);
    this.moveTo(lerp(current, desired, t), viewer);
    const settled = sub(v3(this.target.position.x, this.target.position.y, this.target.position.z), desired);
    return dot(settled, settled) < 0.05 * 0.05;
  }

  private moveTo(position: Vec3, viewer: ViewerPose): void {
    this.target.position.set(position.x, position.y, position.z);
    // Face the viewer: the panel's +Z faces the camera in three.js, so look
    // AT the viewer rather than away (the Unity port looks away because uGUI
    // canvases face -Z).
    const look = sub(position, viewer.position);
    if (dot(look, look) > 0.0001) this.target.lookAt(viewer.position.x, viewer.position.y, viewer.position.z);
  }

  /**
   * The window's target point: straight ahead at the configured distance,
   * with its yaw clamped away from any active obstacle - the sliding-door
   * stop at the clearance boundary.
   */
  desiredPosition(viewer: ViewerPose): Vec3 {
    const forward = flatten(viewer.forward, v3(0, 0, -1));
    let desiredYaw = yawDegrees(forward);

    if (this.options.clearanceDegrees > 0) {
      for (const [key, getPosition] of WindowFollower.obstacles) {
        if (key === this.target) continue;
        const obstacle = getPosition();
        if (!obstacle) continue;
        const toObstacle = v3(obstacle.x - viewer.position.x, 0, obstacle.z - viewer.position.z);
        if (dot(toObstacle, toObstacle) < 0.01) continue;

        const obstacleYaw = yawDegrees(normalize(toObstacle));
        const delta = deltaAngle(obstacleYaw, desiredYaw);
        if (Math.abs(delta) >= this.options.clearanceDegrees) continue;

        // Inside the label's cone - stop at the boundary. The side is the
        // WINDOW's current side (not the gaze side), so the window stays
        // parked while the gaze sweeps across the label and only crosses over
        // once the gaze clears the far boundary.
        const side = this.currentSideOf(obstacleYaw, viewer.position, delta);
        desiredYaw = obstacleYaw + side * this.options.clearanceDegrees;
      }
    }

    const direction = directionFromYaw(desiredYaw);
    return add(
      add(viewer.position, scale(direction, this.options.distanceMeters)),
      scale(UP, this.options.heightOffsetMeters),
    );
  }

  /** Which side of the obstacle the window is on right now (+1 right, -1 left). */
  private currentSideOf(obstacleYaw: number, viewerPosition: Vec3, fallback: number): number {
    const toWindow = v3(this.target.position.x - viewerPosition.x, 0, this.target.position.z - viewerPosition.z);
    if (dot(toWindow, toWindow) < 0.01) return fallback !== 0 ? Math.sign(fallback) : 1;
    const windowYaw = yawDegrees(normalize(toWindow));
    const delta = deltaAngle(obstacleYaw, windowYaw);
    if (delta === 0) return fallback !== 0 ? Math.sign(fallback) : 1;
    return delta >= 0 ? 1 : -1;
  }

  /** The window ended up behind the user, unreasonably far away, or at the wrong height. */
  private isLost(viewer: ViewerPose): boolean {
    const toWindow = sub(v3(this.target.position.x, this.target.position.y, this.target.position.z), viewer.position);
    if (length(toWindow) > Math.max(3, this.options.distanceMeters * 2.5)) return true;

    // Anchored well above or below eye level - happens when the window was
    // placed before head tracking settled.
    if (Math.abs(toWindow.y - this.options.heightOffsetMeters) > 0.5) return true;

    const forward = flatten(viewer.forward, v3(0, 0, -1));
    const flatToWindow = v3(toWindow.x, 0, toWindow.z);
    return dot(flatToWindow, flatToWindow) > 0.001 && dot(forward, normalize(flatToWindow)) < -0.1;
  }
}
